import { IComponentType, IComponentUnion, ISceneComp, IScreenContent } from '@r3f-component-data-structure';
import { hlsVideoCache } from '@r3f/hooks';
import { useDataStore } from '@/zustand';
import { Matrix4, PerspectiveCamera, Vector3 } from 'three';

export const getScreenRelativeCameraPosition = (zapparCamera: PerspectiveCamera, contentScale: Vector3, contentCenter: Vector3, aspect: number) => {
	const fov = getFOVFromProjectionMatrix(zapparCamera.projectionMatrix);
	const cameraDirection = new Vector3(0, 0, -1);
	const distanceFromCamToFitHeight = calculateDistanceFromHeight(fov, contentScale.y);
	const distanceFromCamToFitWidth = calculateDistanceFromWidth(fov, contentScale.x, aspect);
	const distance = Math.max(distanceFromCamToFitHeight, distanceFromCamToFitWidth);
	const screenRelativePosition = new Vector3(0, 0, 0).add(cameraDirection.multiplyScalar(distance));
	screenRelativePosition.y = contentCenter.y;
	screenRelativePosition.z = -screenRelativePosition.z + contentCenter.z + contentScale.z / 2;
	screenRelativePosition.z = screenRelativePosition.z * 1.2; // add padding around scene
	return screenRelativePosition;
};

export const getScreenRelativeContentPosition = (zapparCamera: PerspectiveCamera, contentScale: Vector3, contentCenter: Vector3, aspect: number) => {
	const fov = getFOVFromProjectionMatrix(zapparCamera.projectionMatrix);
	const cameraDirection = new Vector3(0, 0, -1);
	const distanceFromCamToFitHeight = calculateDistanceFromHeight(fov, contentScale.y);
	const distanceFromCamToFitWidth = calculateDistanceFromWidth(fov, contentScale.x, aspect);
	const distance = Math.max(distanceFromCamToFitHeight, distanceFromCamToFitWidth);
	const screenRelativePosition = new Vector3(0, 0, 0).add(cameraDirection.multiplyScalar(distance));
	screenRelativePosition.y = -contentCenter.y / 2;
	screenRelativePosition.z = screenRelativePosition.z - contentCenter.z - contentScale.z / 2;
	screenRelativePosition.z = screenRelativePosition.z * 1.4; // add padding around scene
	return screenRelativePosition;
};

export const calculateDistanceFromHeight = (fov: number, height: number) => {
	// Convert fov to radians and calculate distance
	const distance = height / (2 * Math.tan((fov * Math.PI) / 360));
	return distance;
};

const calculateDistanceFromWidth = (fov: number, width: number, aspect: number) => {
	// Convert fov to radians and calculate distance
	return width / aspect / (2 * Math.tan((fov * Math.PI) / 360));
};

export const getFOVFromProjectionMatrix = (projectionMatrix: Matrix4) => {
	return (2 * Math.atan(1 / projectionMatrix.elements[5]) * 180) / Math.PI;
};

export const calculateHeightByDistanceAndAngle = (distance: number, angleInDegrees: number) => {
	// Convert angle from degrees to radians
	const angleInRadians = angleInDegrees * (Math.PI / 180);

	// Calculate height using the tangent function
	const height = distance * Math.tan(angleInRadians);

	return height;
};

const isVideoPlaying = (video?: HTMLVideoElement) => {
	return (
		video && !video.paused && !video.ended && video.readyState >= 3 // HAVE_FUTURE_DATA
	);
};

export const getPlayingVideoIds = () => {
	const playingVideoIds: string[] = [];
	for (const key in hlsVideoCache) {
		const hlsData = hlsVideoCache[key];
		const video = hlsData[1];
		if (isVideoPlaying(video)) {
			playingVideoIds.push(key);
		}
	}
	return playingVideoIds;
};

const getSceneScreenContentId = (sceneId: string, componentsById: {[id: string]: IComponentUnion}): string => {
	const activeSceneChildren = (componentsById[sceneId] as ISceneComp)?.children || [];
	return activeSceneChildren.filter(id => componentsById[id].type === IComponentType.ScreenContent)[0]
}

export const getScreenRelativeComponentIds = (sceneId: string, componentsById: {[id: string]: IComponentUnion} | null): string[] => {  
    if (!componentsById) return [];
    const sceneContentId = getSceneScreenContentId(sceneId, componentsById)
    const sceneContent = componentsById[sceneContentId] as IScreenContent;

    return sceneContent.children.reduce((screenContentIds, anchorId) => {
        const child = componentsById[anchorId];
        if (!child) return screenContentIds;
        if (child.type === IComponentType.ScreenAnchorGroup) {
            screenContentIds.push(...child.children);
        }
        return screenContentIds;
    }, [] as string[])
}

export const getVideoIdsBySceneId = (sceneId: string) => {
    const componentsById = useDataStore.getState().componentsById;
    const srContentIds = getScreenRelativeComponentIds(sceneId, componentsById);
    const arContentIds = (componentsById?.[sceneId] as ISceneComp | undefined)?.children ?? [];
    return [...srContentIds, ...arContentIds].filter(id => componentsById?.[id]?.type === IComponentType.Video)
}